import { MantineThemeOverride } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { generateColorPalette } from 'shared/utils'
import { FontSize, useCustomizerStore } from 'store/customizerStore'
import { getSystemColorScheme } from 'utils/getSystemColorScheme'

export type ColorScheme = 'light' | 'dark'

export const theme = (
  colorScheme: ColorScheme,
  primaryColor: string,
  fontSize: FontSize,
): MantineThemeOverride =>
  ({
    colorScheme,
    dir: 'ltr',
    fn: {
      fontStyles: () => ({
        fontFamily: 'Poppins, sans-serif',
      }),
      largerThan: (breakpoint: string) =>
        `@media (min-width: var(--mantine-breakpoint-${breakpoint}))`,
      variant: () => ({
        color: colorScheme === 'dark' ? '#f8fafc' : '#1f2937',
        background: 'var(--cl-app-surface-elevated)',
        border: '1px solid var(--cl-app-border)',
      }),
    },
    colors: {
      brand: generateColorPalette(primaryColor),
    },
    primaryColor: 'brand',
    fontFamily: 'Poppins, sans-serif',
    headings: {
      fontFamily: 'Poppins, sans-serif',
    },
    components: {
      Button: {
        defaultProps: {
          size: 'sm',
          radius: 'md',
        },
      },
      Card: {
        defaultProps: {
          shadow: 'sm',
          bg: 'var(--cl-app-surface-elevated)',
          style: {
            borderColor: 'var(--cl-app-border)',
            overflow: 'visible',
          },
        },
      },
      Paper: {
        defaultProps: {
          bg: 'var(--cl-app-surface-elevated)',
          style: {
            borderColor: 'var(--cl-app-border)',
          },
        },
      },
      TextInput: {
        defaultProps: {
          radius: 'md',
        },
      },
      PasswordInput: {
        defaultProps: {
          radius: 'md',
        },
      },
      NumberInput: {
        defaultProps: {
          radius: 'md',
        },
      },
      Textarea: {
        defaultProps: {
          radius: 'md',
        },
      },
      Select: {
        defaultProps: {
          radius: 'md',
        },
      },
      MultiSelect: {
        defaultProps: {
          radius: 'md',
        },
      },
      TagsInput: {
        defaultProps: {
          radius: 'md',
        },
      },
      Badge: {
        defaultProps: {
          radius: 'xl',
        },
      },
      Tooltip: {
        defaultProps: {
          zIndex: 1002,
        },
      },
      Drawer: {
        defaultProps: {
          zIndex: 1002,
        },
      },
    },
    white: '#f8f9fa',
    black: '#333',
    defaultGradient: {
      deg: 45,
      from: 'brand.5',
      to: 'brand.6',
    },
    other: {
      fontSize,
    },
  } as MantineThemeOverride)

export const useCreateTheme = () => {
  const [systemColor, setSystemColor] =
    useState<ColorScheme>(getSystemColorScheme)
  const presetColor = useCustomizerStore(state => state.colorScheme)
  const primaryColor = useCustomizerStore(state => state.primaryColor)
  const fontSize = useCustomizerStore(state => state.fontSize)

  useEffect(() => {
    if (!window.matchMedia) return

    const onSystemThemeChange = (e: MediaQueryListEvent) => {
      setSystemColor(e.matches ? 'dark' : 'light')
    }

    const scheme = window.matchMedia('(prefers-color-scheme: dark)')

    scheme.addEventListener('change', onSystemThemeChange)

    return () => {
      scheme.removeEventListener('change', onSystemThemeChange)
    }
  }, [])

  const colorScheme = presetColor === 'system' ? systemColor : presetColor

  return useMemo(
    () => theme(colorScheme, primaryColor, fontSize),
    [colorScheme, primaryColor, fontSize],
  )
}
